import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Navigation, NavigationNode } from '~/lib/data-contract';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import ArrowLeft from '~/icons/chevron-left.svg';
import Close from '~/icons/close.svg';
import styles from './MegaMenu.module.scss';
import { Image, LinkProps, Link as SharedLink } from '~/shared/components';
import Typography from '~/shared/components/Typography/Typography';
import { useTranslation } from '~/shared/utils/translation';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';

const DATA_THIRD_MENU = 'data-third-menu-open';

export const closeMenu = () => {
    document.documentElement.removeAttribute('data-menu-open');
    document.documentElement.removeAttribute(DATA_THIRD_MENU);
    document
        .querySelector(`.${styles.subNavItem}.${styles.active}`)
        ?.classList.remove(styles.active);
    document
        .querySelector(`.${styles.mainNavItem}.${styles.active}`)
        ?.classList.remove(styles.active);

    document.querySelector(`.${styles.mainNavList}`)?.scrollTo(0, 0);
    document
        .querySelectorAll(`.${styles.subNav}, .${styles.subSubNav}`)
        .forEach((el) => el.scrollTo(0, 0));
};

const Link = (props: LinkProps) => {
    return <SharedLink {...props} onClick={closeMenu} />;
};

const navHeaderBar = (onClickBack?: () => void, title?: string, hasImage?: boolean) => (
    <div className={styles.navHeaderBar}>
        <button className={styles.navHeaderBack} onClick={onClickBack} aria-label="back">
            <ArrowLeft />
        </button>
        {!hasImage && (
            <Typography variant="title-XXS" weight="regular" className={styles.menuHeaderTitle}>
                {title}
            </Typography>
        )}
        <button className={styles.navHeaderClose} onClick={closeMenu} aria-label="close">
            <Close />
        </button>
    </div>
);

const navHeader = (
    data: NavigationNode,

    onClickBack?: () => void,
) => {
    return (
        <>
            {navHeaderBar(onClickBack, data.link.title, !!data.image?.src)}

            {data.image?.src && (
                <figure className={styles.navHeader}>
                    <DynamicImageLoader
                        src={data.image?.src ?? ''}
                        height={321}
                        width={834}
                        sizes="100vw"
                        alt=""
                    />

                    <figcaption>
                        <Typography
                            variant="title-XXS"
                            weight="regular"
                            className={styles.menuHeaderTitle}
                        >
                            {data.link.title}
                        </Typography>
                        <Link href={data.link.url}>{data.alternateLinkHeader}</Link>
                    </figcaption>
                </figure>
            )}
        </>
    );
};

const SubSubNavList = ({
    items,
    isExpanded,
    onExpand,
}: {
    items: NavigationNode[];
    isExpanded: boolean;
    onExpand: () => void;
}) => {
    const [canExpand, setCanExpand] = useState(false);
    const listRef = useRef<HTMLUListElement>(null);
    const { translate } = useTranslation();

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const updateCanExpand = () => {
            const lastItem = list.lastElementChild;
            if (!lastItem || !list.clientWidth) return;

            const hasItemsInHiddenColumns =
                lastItem.getBoundingClientRect().left >= list.getBoundingClientRect().right;

            setCanExpand(hasItemsInHiddenColumns || list.scrollHeight > list.clientHeight);
        };

        const resizeObserver = new ResizeObserver(updateCanExpand);

        resizeObserver.observe(list);
        document.fonts.ready.then(updateCanExpand);

        return () => resizeObserver.disconnect();
    }, []);

    return (
        <>
            <div className={styles.subSubNavListWrapper}>
                <ul
                    ref={listRef}
                    className={clsx(styles.subSubNavList, isExpanded && styles.expanded)}
                >
                    {items.map((item, index) => (
                        <li key={index} className={styles.subSubNavItem}>
                            <Link href={item.link.url} className={styles.subSubNavLink}>
                                <span className={styles.subNavLabel}>{item.link?.text}</span>

                                {!!item.image?.src && (
                                    <DynamicImageLoader
                                        className={styles.linkImage}
                                        height={100}
                                        width={100}
                                        src={`${item.image?.src}&noimageindex=true`}
                                        alt={item.image?.alt ?? 'Navigation image'}
                                    />
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>

                {canExpand && !isExpanded && (
                    <button
                        type="button"
                        className={styles.subSubNavFade}
                        aria-hidden="true"
                        tabIndex={-1}
                        onClick={onExpand}
                    />
                )}
            </div>

            {canExpand && !isExpanded && (
                <button type="button" className={styles.viewAllButton} onClick={onExpand}>
                    {translate('navigation.mobile.mainMenu.viewAll')}
                </button>
            )}
        </>
    );
};

const thirdLevelItems = (
    data: NavigationNode | undefined,
    isExpanded: boolean,
    onExpand: () => void,
) => {
    if (!data?.children?.length) return null;

    return (
        <div className={styles.subSubNav}>
            {navHeader(data, () => {
                document
                    .querySelector(`.${styles.subNavItem}.${styles.active}`)
                    ?.classList.remove(styles.active);

                document.documentElement.removeAttribute(DATA_THIRD_MENU);
            })}

            <SubSubNavList items={data.children} isExpanded={isExpanded} onExpand={onExpand} />
        </div>
    );
};

const SUB_NAV_COLUMNS = 4;

const SecondLevelDropdown = ({
    data,
    dataIndex,
    isOpen,
    onRequestClose,
    onHoverInside,
    onHoverOutside,
}: {
    data: NavigationNode;
    dataIndex: number;
    isOpen: boolean;
    onRequestClose: () => void;
    onHoverInside: () => void;
    onHoverOutside: () => void;
}) => {
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [wasOpen, setWasOpen] = useState(isOpen);
    const subNavRef = useRef<HTMLDivElement>(null);

    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            setExpandedRows(new Set());
        }
    }

    useEffect(() => {
        if (isOpen) {
            subNavRef.current?.scrollTo(0, 0);
        }
    }, [isOpen]);

    if (!data.children?.length) return null;

    const rowCount = Math.ceil(data.children.length / SUB_NAV_COLUMNS);

    const expandRow = (row: number) => {
        setExpandedRows((prev) => {
            if (row === 0) {
                return new Set(Array.from({ length: rowCount }, (_, i) => i));
            }

            return new Set(prev).add(row);
        });
    };

    return (
        <>
            <div className={styles.subNavBackdrop} onMouseEnter={onHoverOutside} />

            <div
                ref={subNavRef}
                className={styles.subNav}
                tabIndex={-1}
                onMouseEnter={onHoverInside}
                data-testid={`dropdown-container-${dataIndex}`}
            >
                {navHeader(data, () =>
                    document
                        .querySelector(`.${styles.mainNavItem}.${styles.active}`)
                        ?.classList.remove(styles.active),
                )}

                <div className={styles.subNavHeader}>
                    <Typography
                        variant="title-XS"
                        weight="regular"
                        className={styles.subNavHeaderTitle}
                    >
                        {data.link.title}
                    </Typography>
                    <button
                        type="button"
                        className={styles.subNavHeaderClose}
                        onClick={onRequestClose}
                        aria-label="close"
                    >
                        <Close />
                    </button>
                </div>

                <ul className={styles.subNavList}>
                    {data.children.map((item, index) => {
                        const row = Math.floor(index / SUB_NAV_COLUMNS);

                        return (
                            <li key={index} className={styles.subNavItem}>
                                <div className={styles.subNavImage}>
                                    <DynamicImageLoader
                                        className={styles.image}
                                        height={500}
                                        width={500}
                                        sizes="25vw"
                                        src={`${item.image?.src}&noimageindex=true`}
                                        alt={item.image?.alt ?? 'Navigation image'}
                                    />
                                </div>

                                {!!item.children?.length && (
                                    <div
                                        className={styles.subNavLinkMenu}
                                        onClick={(e) => {
                                            e.currentTarget.parentElement?.classList.add(
                                                styles.active,
                                            );
                                            document.documentElement.setAttribute(
                                                DATA_THIRD_MENU,
                                                '',
                                            );
                                        }}
                                    >
                                        <span className={styles.subNavLabel}>
                                            {item.link.title}
                                        </span>

                                        {!!item.mobileImage?.src && (
                                            <DynamicImageLoader
                                                className={styles.linkImage}
                                                height={100}
                                                width={100}
                                                src={`${item.mobileImage?.src}&noimageindex=true`}
                                                alt={item.mobileImage?.alt ?? 'Navigation image'}
                                            />
                                        )}
                                    </div>
                                )}

                                <Link href={item.link.url} className={styles.subNavLink}>
                                    <span className={styles.subNavLabel}>{item.link.title}</span>

                                    {!!item.mobileImage?.src && (
                                        <DynamicImageLoader
                                            className={styles.linkImage}
                                            height={100}
                                            width={100}
                                            src={`${item.mobileImage?.src}&noimageindex=true`}
                                            alt={item.mobileImage?.alt ?? 'Navigation image'}
                                        />
                                    )}
                                </Link>

                                {thirdLevelItems(item, expandedRows.has(row), () => expandRow(row))}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
};

const MegaMenu = ({ navigation }: { navigation?: Navigation }) => {
    const { mainMenu = [], metaMenu } = navigation || {};
    const { translate } = useTranslation();
    const isMobile = useIsMobile();

    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [isClosingInstantly, setIsClosingInstantly] = useState(false);

    const openOnHover = (index: number) => {
        if (isMobile) return;
        setIsClosingInstantly(false);
        setOpenIndex(index);
    };

    const closeOnHoverOut = () => {
        if (isMobile) return;
        setOpenIndex(null);
    };

    useEffect(() => {
        const mainNavigation = document.getElementById('mainNavigation');
        const mainHeader = document.getElementById('mainHeader');

        if (!mainNavigation || !mainHeader) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            document.documentElement.style.setProperty(
                '--mainNavigation-height',
                `${Math.round(entry.contentRect.height)}px`,
            );
        });

        const resizeObserverMainHeader = new ResizeObserver((entries) => {
            const entry = entries[0];
            document.documentElement.style.setProperty(
                '--mainHeader-height',
                `${Math.round(entry.contentRect.height)}px`,
            );
        });

        resizeObserver.observe(mainNavigation);
        resizeObserverMainHeader.observe(mainHeader);

        return () => {
            resizeObserver.disconnect();
            resizeObserverMainHeader.disconnect();
        };
    }, []);

    return (
        <nav
            className={clsx(styles.component, isClosingInstantly && styles.closeInstantly)}
            data-testid="main-menu"
            aria-label="navigation"
            onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) {
                    setIsClosingInstantly(true);
                    setOpenIndex(null);
                }
            }}
        >
            <div className={styles.menuHeader}>
                <Typography variant="title-XXS" weight="regular" className={styles.menuHeaderTitle}>
                    {translate('header.menu')}
                </Typography>
                <button className={styles.menuHeaderClose} onClick={closeMenu} aria-label="close">
                    <Close />
                </button>
            </div>

            <ul className={styles.mainNavList}>
                {mainMenu.map((item, index) => {
                    if (!item.link.title) return null;

                    return (
                        <li
                            key={index}
                            className={clsx(styles.mainNavItem, openIndex === index && styles.open)}
                            data-testid={`navigation-item-${index}`}
                            onMouseEnter={() => openOnHover(index)}
                            onMouseLeave={closeOnHoverOut}
                            onFocus={() => openOnHover(index)}
                        >
                            {!!item.children?.length && (
                                <span
                                    className={styles.mainNavTitle}
                                    onClick={(e) => {
                                        e.currentTarget.parentElement?.classList.add(styles.active);
                                    }}
                                    aria-label={item.link?.title?.toLocaleLowerCase()}
                                >
                                    {item.link.title}
                                </span>
                            )}

                            <Link
                                href={item.link.url}
                                className={styles.mainNavLink}
                                activeClassName={styles.activeLink}
                                aria-label={item.link?.title?.toLocaleLowerCase()}
                            >
                                {item.link.title}
                            </Link>

                            <div className={styles.mainNavImage}>
                                <DynamicImageLoader
                                    className={styles.image}
                                    height={400}
                                    width={400}
                                    sizes="25vw"
                                    src={`${item.image?.src}&noimageindex=true`}
                                    alt={item.image?.alt ?? 'Navigation image'}
                                />
                            </div>

                            <SecondLevelDropdown
                                data={item}
                                dataIndex={index}
                                isOpen={openIndex === index}
                                onRequestClose={() => setOpenIndex(null)}
                                onHoverInside={() => openOnHover(index)}
                                onHoverOutside={closeOnHoverOut}
                            />
                        </li>
                    );
                })}

                {metaMenu?.mobileLinks &&
                    metaMenu.mobileLinks.map((item, linkIndex) => {
                        return (
                            <li key={linkIndex} className={styles.metaItem}>
                                <Link
                                    href={item.link.url}
                                    className={styles.metaLink}
                                    aria-label={item.link?.title?.toLocaleLowerCase()}
                                >
                                    {!!item.image?.src && (
                                        <Image
                                            src={item.image.src}
                                            width={16}
                                            height={16}
                                            layout="intrinsic"
                                            alt={item.image?.alt ?? ''}
                                            disableSkeleton
                                        />
                                    )}
                                    <Typography variant="textLink-M">{item.link.title}</Typography>
                                </Link>
                            </li>
                        );
                    })}

                {metaMenu?.bottomLink?.url && (
                    <li className={styles.metaBottom}>
                        <Link
                            href={metaMenu.bottomLink.url}
                            className={styles.metaBottomLink}
                            aria-label={metaMenu.bottomLink?.title?.toLocaleLowerCase()}
                        >
                            <Typography variant="title-XS" weight="bold">
                                {metaMenu.bottomLink.title}
                            </Typography>
                        </Link>
                    </li>
                )}
            </ul>
        </nav>
    );
};

export default MegaMenu;
